import type { ComponentType, ReactNode } from "react";
import {
  FormLanguageSelector,
  type LanguageOption,
} from "../ui/form_language_selector";
import { InputGroup } from "../ui/input_group";
import { DropdownMultiTypeahead } from "../ui/dropdown_multi_typeahead";
import { RatingStars } from "../ui/rating_stars";
import type { City, ServiceCategory } from "../types/entities";
import type {
  DropdownMultiTypeaheadUiStrings,
  FreelancerFilterModalMessages,
} from "../types/freelancer_modal_messages";

type ButtonComponent = ComponentType<{
  className?: string;
  onClick?: () => void;
  children?: ReactNode;
}>;

type BlockingModalComponent = ComponentType<{
  className?: string;
  children: ReactNode;
}>;

export type FreelancerFilterModalProps = {
  className?: string;
  rating: number;
  selectedLanguages: string[];
  selectedServices: ServiceCategory[];
  selectedCities: City[];
  proximity: number;
  isBlocking?: boolean;
  onClose: () => void;
  onSubmit: () => void;
  setSelectedLanguages: (languages: string[]) => void;
  setSelectedServices: (services: ServiceCategory[]) => void;
  setSelectedCities: (cities: City[]) => void;
  setProximity: (proximity: number) => void;
  setRating: (rating: number) => void;
  serviceCategoryOptions: ServiceCategory[];
  cityOptions: City[];
  messages: FreelancerFilterModalMessages;
  dropdownUiStrings: DropdownMultiTypeaheadUiStrings;
  groupBy?: (item: ServiceCategory) => string;
  reviewsEnabled: boolean;
  proximitySearchEnabled: boolean;
  languageOptions: LanguageOption[];
  Button: ButtonComponent;
  FullBlockingModal: BlockingModalComponent;
};

export function FreelancerFilterModal({
  selectedLanguages,
  selectedServices,
  selectedCities,
  proximity,
  rating,
  className,
  isBlocking = true,
  onClose,
  onSubmit,
  setSelectedLanguages,
  setSelectedServices,
  setSelectedCities,
  setProximity,
  setRating,
  serviceCategoryOptions,
  cityOptions,
  messages,
  dropdownUiStrings,
  groupBy,
  reviewsEnabled,
  proximitySearchEnabled,
  languageOptions,
  Button,
  FullBlockingModal,
}: FreelancerFilterModalProps) {
  const TopDiv = isBlocking ? FullBlockingModal : "div";

  return (
    <TopDiv className={className}>
      <h3 className="mb-4 text-secondary md:hidden">{messages.filterBy}</h3>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:gap-8">
          <FormLanguageSelector
            label={messages.languagesSpoken}
            value={selectedLanguages}
            onChange={setSelectedLanguages}
            languageOptions={languageOptions}
            optionWrapperClassName="mt-4"
          />

          <div className="md:min-w-[16rem]">
            <InputGroup label={messages.selectService}>
              <DropdownMultiTypeahead
                selected={selectedServices}
                onChange={setSelectedServices}
                options={serviceCategoryOptions}
                getOptionLabel={(item) => item.label}
                getOptionValue={(item) => item.id}
                placeholder={messages.addService}
                groupBy={groupBy}
                inputMode="select"
                uiStrings={dropdownUiStrings}
              />
            </InputGroup>
          </div>

          <div className="md:min-w-[16rem]">
            <InputGroup label={messages.city}>
              <DropdownMultiTypeahead
                selected={selectedCities}
                onChange={setSelectedCities}
                options={cityOptions}
                getOptionLabel={(item) => item.name}
                getOptionValue={(item) => item.id}
                placeholder={messages.addCity}
                className="mt-2 md:mt-0"
                inputMode="select"
                uiStrings={dropdownUiStrings}
              />
            </InputGroup>
          </div>
        </div>

        <div className="flex flex-col gap-4 md:flex-row md:gap-8">
          {reviewsEnabled && (
            <InputGroup label={messages.rating}>
              <RatingStars rating={rating} onChange={setRating} />
            </InputGroup>
          )}
        </div>

        <div className="flex flex-col gap-4">
          {proximitySearchEnabled && (
            <InputGroup label={messages.proximity}>
              <input
                type="range"
                min={0}
                max={100}
                value={proximity}
                onChange={(e) => setProximity(Number(e.target.value))}
                className="custom-range mt-2 w-full md:mt-4"
              />
              <div className="flex justify-between text-xs text-gray-400">
                <span>{proximity} km</span>
                <span>{messages.maximum}</span>
              </div>
            </InputGroup>
          )}
        </div>

        <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
          <Button
            onClick={onSubmit}
            className="mt-4 w-full md:mt-0 md:w-auto md:px-8"
          >
            {messages.showResults}
          </Button>

          <button
            type="button"
            className="mx-auto block font-bold leading-tight text-secondary underline md:mx-16"
            onClick={onClose}
          >
            {messages.close}
          </button>
        </div>
      </div>
    </TopDiv>
  );
}
